import { useState } from "react";
import { Search, Sparkles } from "lucide-react";
import { Spinner } from "../Spinner";

export function MealAlternativesPicker({ meals, onGetAlternatives }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMealId, setSelectedMealId] = useState(null);
  const [note, setNote] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const filteredMeals = (meals || []).filter((meal) =>
    meal.mealName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleGetAlternatives = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      await onGetAlternatives(selectedMealId, note);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Couldn't generate alternatives for that meal. Please try again.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-7">
      <label className="block text-sm font-medium text-gray-900 mb-2">
        Which meal do you want alternatives to?
      </label>

      {isGenerating ? (
        <div className="flex flex-col items-center justify-center gap-3 py-6">
          <Spinner size="lg" className="text-gray-900" />
          <p className="text-sm font-medium text-gray-600">
            Finding some alternatives...
          </p>
        </div>
      ) : (
        <>
          <div className="relative mb-3">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your recipes"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>

          <div className="max-h-56 overflow-auto flex flex-col gap-1.5 mb-3">
            {filteredMeals.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">
                No recipes match that search.
              </p>
            ) : (
              filteredMeals.map((meal) => (
                <button
                  key={meal.mealId}
                  type="button"
                  onClick={() => setSelectedMealId(meal.mealId)}
                  className={`text-left px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    selectedMealId === meal.mealId
                      ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {meal.mealName}
                </button>
              ))
            )}
          </div>

          {selectedMealId && (
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Anything specific? (optional)
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={300}
                placeholder={`e.g. "keep it a sandwich" or "make it Indian-inspired"`}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              />
            </div>
          )}

          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleGetAlternatives}
              disabled={!selectedMealId}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4" />
              Get alternatives
            </button>
          </div>
        </>
      )}
    </div>
  );
}
